import * as yup from 'yup';
import type {
  CreateGameRequest,
  Game,
  GameDialogDefaultValues,
  GameFormData,
  GameFormErrors,
  UpdateGameRequest,
} from '../../../../types/features/games';
import type { SimpleState } from '../../../../types/locations';
import { formatLabelWithCode } from '../../../../utils/locationFormat';
import { gameDialogSchema } from '../validators/gameDialogSchema';

const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

const toOptionalInt = (value: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toOptionalString = (value: string): string | undefined => (value ? value : undefined);

const normalizePendingPluVerification = (value: boolean | undefined): boolean => value ?? false;

export function derivePricePerPack(ticketsPerPack: string, costPerTicket: string): string {
  const tickets = Number(ticketsPerPack);
  const price = Number(costPerTicket);

  if (Number.isNaN(tickets) || tickets <= 0 || Number.isNaN(price)) {
    return '';
  }

  return (tickets * price).toFixed(2);
}

export const createEmptyGameFormData = (defaultValues?: GameDialogDefaultValues): GameFormData => {
  const ticketsPerPack = defaultValues?.tickets_per_pack || '';
  const costPerTicket = defaultValues?.cost_per_ticket || '';

  return {
    country_code: defaultValues?.country_code || '',
    game_id: defaultValues?.game_id || '',
    state_code: defaultValues?.state_code || '',
    plu: defaultValues?.plu || '',
    name: '',
    tickets_per_pack: ticketsPerPack,
    price_per_pack: defaultValues?.price_per_pack || derivePricePerPack(ticketsPerPack, costPerTicket),
    cost_per_ticket: costPerTicket,
    start_date: '',
    end_date: '',
    image_url: '',
    pending_plu_verification: true,
  };
};

export const formatGameDateForInput = (dateString: string | null): string => {
  if (!dateString) {
    return '';
  }

  try {
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

export const createGameFormDataFromGame = (game: Game): GameFormData => {
  const ticketsPerPackValue = game.tickets_per_pack?.toString() || '';
  const pricePerPackValue = game.cost_per_ticket && game.tickets_per_pack
    ? (Number(game.cost_per_ticket) * game.tickets_per_pack).toFixed(2)
    : '';

  return {
    country_code: game.country_code || '',
    game_id: game.game_id.toString(),
    state_code: game.state_code?.toString() || '',
    plu: game.plu?.toString() || '',
    name: game.name,
    tickets_per_pack: ticketsPerPackValue,
    price_per_pack: pricePerPackValue,
    cost_per_ticket: game.cost_per_ticket.toString(),
    start_date: formatGameDateForInput(game.start_date),
    end_date: formatGameDateForInput(game.end_date),
    image_url: '',
    pending_plu_verification: normalizePendingPluVerification(game.pending_plu_verification),
  };
};

export const getCountriesFromStates = (states: SimpleState[]): string[] => {
  const countryCodes = new Set<string>();

  states.forEach((state) => {
    if (state.country_code) {
      countryCodes.add(state.country_code);
    }
  });

  return Array.from(countryCodes).sort();
};

export const getStatesForCountry = (states: SimpleState[], countryCode: string): SimpleState[] =>
  countryCode ? states.filter((state) => state.country_code === countryCode) : [];

export const getGameCountryDisplayLabel = (
  states: SimpleState[],
  countryCode: string,
  countryName?: string,
): string => {
  if (countryName) {
    return countryName;
  }

  const stateWithCountryName = states.find((state) => state.country_code === countryCode && state.country_name);

  return stateWithCountryName?.country_name || countryCode || 'N/A';
};

export const getGameStateDisplayLabel = (
  states: SimpleState[],
  stateCode: string,
  stateName?: string,
): string => {
  const stateDetails = states.find((state) => state.state_code === stateCode);
  const displayStateName = stateName || stateDetails?.state_name;
  const displayStateCode = stateCode || stateDetails?.state_code;

  if (displayStateName && displayStateCode) {
    return `${displayStateName}(${displayStateCode})`;
  }

  return formatLabelWithCode(displayStateName, displayStateCode);
};

export const updateGameFormData = (
  previousFormData: GameFormData,
  field: keyof GameFormData,
  value: string | boolean,
): GameFormData => {
  const nextFormData = {
    ...previousFormData,
    [field]: value,
  } as GameFormData;

  if (field === 'tickets_per_pack' || field === 'cost_per_ticket') {
    nextFormData.price_per_pack = derivePricePerPack(
      field === 'tickets_per_pack' ? String(value) : nextFormData.tickets_per_pack,
      field === 'cost_per_ticket' ? String(value) : nextFormData.cost_per_ticket,
    );
  }

  if (field === 'country_code') {
    nextFormData.state_code = '';
  }

  return nextFormData;
};

export const validateSelectedImage = (file: File | null): string => {
  if (!file) {
    return '';
  }

  if (!file.type.startsWith('image/')) {
    return 'Please select a valid image file';
  }

  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return 'Image file size must be less than 10MB';
  }

  return '';
};

export const validateGameForm = async (formData: GameFormData, selectedImage: File | null): Promise<GameFormErrors> => {
  const validationErrors: GameFormErrors = {};

  try {
    await gameDialogSchema.validate(formData, { abortEarly: false });
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      error.inner.forEach((entry) => {
        if (entry.path) {
          validationErrors[entry.path] = entry.message;
        }
      });
    }
  }

  const imageError = validateSelectedImage(selectedImage);

  if (imageError) {
    validationErrors.image = imageError;
  }

  return validationErrors;
};

export const buildGameRequestPayload = (
  formData: GameFormData,
  selectedImage: File | null,
): CreateGameRequest | UpdateGameRequest => {
  const ticketsPerPackNumber = parseInt(formData.tickets_per_pack, 10);
  const costPerTicketNumber = Number(parseFloat(formData.cost_per_ticket).toFixed(2));

  return {
    game_id: parseInt(formData.game_id, 10),
    state_code: formData.state_code,
    plu: toOptionalInt(formData.plu),
    name: toOptionalString(formData.name),
    tickets_per_pack: ticketsPerPackNumber,
    cost_per_ticket: costPerTicketNumber,
    start_date: toOptionalString(formData.start_date),
    end_date: toOptionalString(formData.end_date),
    image_url: toOptionalString(formData.image_url),
    image: selectedImage || undefined,
    pending_plu_verification: formData.pending_plu_verification,
  };
};
